import type { SelectionChangedEvent } from "ag-grid-community"
import type { AgGridReact } from "ag-grid-react"
import { useCallback, useEffect, type RefObject } from "react"

import {
  useHighlightSubscription,
  useInspectorSelection,
  useRegisterHighlightStepper,
} from "~/components/design-system/pages"

/**
 * Ties a single-select AG Grid to the Module inspector: the selected row is highlighted,
 * `inspect` selects a row, and the inspector's prev/next steps through the displayed rows.
 * `rows` is the grid's rowData, so the "n of m" position follows filtering.
 */
export function useGridInspectorSelection<Row extends { id: string }>(
  gridRef: RefObject<AgGridReact<Row> | null>,
  rows: readonly Row[],
) {
  const { selectedId, select, clear } = useInspectorSelection()
  const highlight = useHighlightSubscription()

  useEffect(() => {
    highlight.set(new Set(selectedId ? [selectedId] : []))
  }, [highlight, selectedId])

  const inspect = useCallback(
    (id: string) => {
      select(id)
      gridRef.current?.api?.getRowNode(id)?.setSelected(true, true)
    },
    [select, gridRef],
  )

  const { registerStepper, unregisterStepper, setPosition } = useRegisterHighlightStepper()
  useEffect(() => {
    const stepper = {
      step: (delta: 1 | -1) => {
        const api = gridRef.current?.api
        const current = selectedId ? api?.getRowNode(selectedId) : undefined
        const next = api?.getDisplayedRowAtIndex((current?.rowIndex ?? -1) + delta)
        if (!next?.id) return null
        inspect(next.id)
        api?.ensureNodeVisible(next)
        return next.id
      },
      clear,
    }
    registerStepper(stepper)
    return () => unregisterStepper(stepper)
  }, [selectedId, clear, inspect, gridRef, registerStepper, unregisterStepper])

  useEffect(() => {
    const api = gridRef.current?.api
    const node = selectedId ? api?.getRowNode(selectedId) : undefined
    setPosition({ index: node?.rowIndex ?? -1, total: api?.getDisplayedRowCount() ?? 0 })
  }, [selectedId, rows, gridRef, setPosition])

  const onSelectionChanged = useCallback(
    (event: SelectionChangedEvent<Row>) => {
      const picked = event.api.getSelectedRows()[0]
      if (picked) select(picked.id)
    },
    [select],
  )

  return { select, highlight, inspect, onSelectionChanged }
}
